import { create } from 'zustand';
import {
  createClip,
  createComposition,
  createEmptyProject,
  createCrossDissolve,
  createMarker,
  createTrack,
  defaultAudioSettings,
  ensureCompositions,
  flushActiveComposition,
  suggestTrackName,
  type AudioSettings,
  type AudioTrackChannels,
  type Clip,
  type ColorGrade,
  type CompositionSettings,
  type LayerProperties,
  type MediaAsset,
  type PanelId,
  type Project,
  type Transform2D,
  type WindowLayout,
  DEFAULT_PRIMARY_LAYOUT,
  LAYOUT_PRESETS,
} from '@cinegrid/core';
import {
  closeGap,
  expandWithLinked,
  liftDelete,
  linkedClipIds,
  moveClip,
  projectDuration,
  rippleDelete,
  splitClip,
  trimClip,
  type TimelineGap,
} from '../timeline/ops';
import { deleteMediaBlob, forgetAssetUrl } from '../media/mediaStore';

export type Tool = 'select' | 'blade' | 'ripple' | 'hand' | 'text';
export type InspectorTab = 'color' | 'transform' | 'effects' | 'audio' | 'text' | 'keyframes' | 'comp' | 'export';
export type ThemeMode = 'dark' | 'light';

export interface PanelSizes {
  leftWidth: number;
  rightWidth: number;
  timelineHeight: number;
  scopesHeight: number;
}

export const DEFAULT_PANEL_SIZES: PanelSizes = {
  leftWidth: 224,
  rightWidth: 320,
  timelineHeight: 208,
  scopesHeight: 128,
};

export interface EditorState {
  project: Project;
  playhead: number;
  isPlaying: boolean;
  selection: string[];
  selectedGap: TimelineGap | null;
  inPoint: number | null;
  outPoint: number | null;
  tool: Tool;
  timelineZoom: number; // pixels per second
  /** Minimum zoom so the full composition duration fits the timeline viewport. */
  timelineZoomMin: number;
  timelineScrollX: number;
  theme: ThemeMode;
  inspectorTab: InspectorTab;
  layout: WindowLayout;
  tabId: string;
  isLeader: boolean;
  connectedTabs: { tabId: string; label: string; layoutId: string; isLeader: boolean }[];
  snapEnabled: boolean;
  showWindowManager: boolean;
  panelSizes: PanelSizes;
  /** Selected media-bin asset ids (survives panel remounts). */
  mediaSelection: string[];

  // Project
  setProject: (p: Project) => void;
  patchProject: (fn: (p: Project) => Project) => void;
  setComposition: (s: Partial<CompositionSettings>) => void;
  renameProject: (name: string) => void;
  /** Switch active composition / timeline */
  setActiveComposition: (id: string) => void;
  createComposition: (name?: string) => string;
  renameComposition: (id: string, name: string) => void;
  duplicateComposition: (id: string) => string | null;
  removeComposition: (id: string) => void;
  renameTrack: (trackId: string, name: string) => void;
  setTrackHeight: (trackId: string, height: number) => void;
  addVideoTrack: (name: string, relativeToTrackId?: string) => string;
  addAudioTrack: (
    name: string,
    channels: AudioTrackChannels,
    relativeToTrackId?: string
  ) => string;

  // Playback
  setPlayhead: (t: number) => void;
  togglePlay: () => void;
  setPlaying: (v: boolean) => void;
  stepFrame: (dir: -1 | 1) => void;
  goToStart: () => void;
  goToEnd: () => void;
  jumpToNextEdit: (dir: -1 | 1) => void;

  // Markers
  addMarker: (time?: number, label?: string) => void;
  removeMarker: (id: string) => void;
  jumpToMarker: (dir: -1 | 1) => void;

  // Selection / tools
  selectClips: (ids: string[], mode?: 'replace' | 'add' | 'toggle') => void;
  selectGap: (gap: TimelineGap | null) => void;
  deleteSelectedGap: () => void;
  setTool: (t: Tool) => void;
  setTimelineZoom: (z: number) => void;
  setTimelineZoomMin: (z: number) => void;
  setTimelineScrollX: (x: number) => void;
  setInPoint: (t: number | null) => void;
  setOutPoint: (t: number | null) => void;
  /** Set composition duration to the work area (I–O) and shift content to start at 0. */
  trimCompToWorkArea: () => void;
  setTheme: (t: ThemeMode) => void;
  setInspectorTab: (t: InspectorTab) => void;
  setSnapEnabled: (v: boolean) => void;
  setShowWindowManager: (v: boolean) => void;
  setMediaSelection: (ids: string[]) => void;
  setPanelSize: <K extends keyof PanelSizes>(key: K, value: PanelSizes[K]) => void;
  resetPanelSizes: () => void;

  // Media
  addAsset: (asset: MediaAsset) => void;
  removeAsset: (id: string) => void;
  removeAssets: (ids: string[]) => void;
  /** Interpret Footage — conform frame rate (scales duration / clip source times). */
  interpretAssetFrameRate: (assetIds: string[], fps: number) => void;
  createAdjustmentAsset: () => string;
  createSolidAsset: (color?: string) => string;
  addClipFromAsset: (assetId: string, trackId: string, start: number) => void;
  addTextClip: (trackId: string, start: number) => void;

  // Edit ops
  cutAtPlayhead: () => void;
  rippleDeleteSelected: () => void;
  liftDeleteSelected: () => void;
  trimSelected: (edge: 'in' | 'out', time: number, ripple: boolean) => void;
  trimClipAt: (clipId: string, edge: 'in' | 'out', time: number, ripple?: boolean) => void;
  moveSelected: (clipId: string, start: number, trackId?: string) => void;
  /** Move multiple clips by absolute start positions (and optional track for the primary). */
  moveClips: (
    updates: { clipId: string; start: number; trackId?: string }[]
  ) => void;
  unlinkClip: (clipId: string) => void;
  /** Link one selected video clip with one selected audio clip. */
  linkSelectedAv: () => void;
  addCrossDissolveToSelected: () => void;
  updateClipProperties: (clipId: string, props: Partial<LayerProperties>) => void;
  updateClipTransform: (clipId: string, t: Partial<Transform2D>) => void;
  updateClipColor: (clipId: string, grade: Partial<ColorGrade>) => void;
  updateClipAudio: (clipId: string, audio: Partial<AudioSettings>) => void;
  updateClip: (clipId: string, patch: Partial<Clip>) => void;

  // Windowing
  setLayout: (layout: WindowLayout | string) => void;
  setLeader: (v: boolean) => void;
  setConnectedTabs: (tabs: EditorState['connectedTabs']) => void;
  hasPanel: (id: PanelId) => boolean;
}

function loadTheme(): ThemeMode {
  try {
    const t = localStorage.getItem('cinegrid-theme');
    if (t === 'light' || t === 'dark') return t;
  } catch {
    /* ignore */
  }
  return 'dark';
}

function loadPanelSizes(): PanelSizes {
  try {
    const raw = localStorage.getItem('cinegrid-panel-sizes');
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PanelSizes>;
      return { ...DEFAULT_PANEL_SIZES, ...parsed };
    }
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_PANEL_SIZES };
}

function persistPanelSizes(sizes: PanelSizes) {
  try {
    localStorage.setItem('cinegrid-panel-sizes', JSON.stringify(sizes));
  } catch {
    /* ignore */
  }
}

export const useEditorStore = create<EditorState>((baseSet, get) => {
  const set = ((partial: Parameters<typeof baseSet>[0]) => {
    if (typeof partial === 'function') {
      return baseSet((state) => {
        const next = (partial as (s: EditorState) => Partial<EditorState>)(state);
        if (next && typeof next === 'object' && 'project' in next && next.project) {
          return {
            ...next,
            project: flushActiveComposition(ensureCompositions(next.project)),
          };
        }
        return next;
      });
    }
    if (partial && typeof partial === 'object' && 'project' in partial && partial.project) {
      return baseSet({
        ...partial,
        project: flushActiveComposition(ensureCompositions(partial.project)),
      });
    }
    return baseSet(partial);
  }) as typeof baseSet;

  return {
  project: createEmptyProject('CineGrid Project'),
  playhead: 0,
  isPlaying: false,
  selection: [],
  selectedGap: null,
  inPoint: null,
  outPoint: null,
  tool: 'select',
  timelineZoom: 80,
  timelineZoomMin: 10,
  timelineScrollX: 0,
  theme: loadTheme(),
  inspectorTab: 'transform',
  layout: DEFAULT_PRIMARY_LAYOUT,
  tabId: crypto.randomUUID(),
  isLeader: false,
  connectedTabs: [],
  snapEnabled: true,
  showWindowManager: false,
  panelSizes: loadPanelSizes(),
  mediaSelection: [],

  setProject: (p) => set({ project: p }),
  patchProject: (fn) => set({ project: fn(get().project) }),
  setComposition: (s) =>
    set({
      project: {
        ...get().project,
        settings: { ...get().project.settings, ...s },
        updatedAt: Date.now(),
      },
    }),
  renameProject: (name) =>
    set({ project: { ...get().project, name, updatedAt: Date.now() } }),

  setActiveComposition: (id) => {
    const flushed = flushActiveComposition(get().project);
    const next = flushed.compositions.find((c) => c.id === id);
    if (!next || flushed.activeCompositionId === id) {
      set({ project: flushed });
      return;
    }
    set({
      project: {
        ...flushed,
        activeCompositionId: next.id,
        settings: next.settings,
        tracks: next.tracks,
        clips: next.clips,
        markers: next.markers,
        updatedAt: Date.now(),
      },
      playhead: 0,
      isPlaying: false,
      selection: [],
      selectedGap: null,
      inPoint: null,
      outPoint: null,
    });
  },

  createComposition: (name) => {
    const flushed = flushActiveComposition(get().project);
    const n = flushed.compositions.length + 1;
    const comp = createComposition(name?.trim() || `Comp ${n}`);
    set({
      project: {
        ...flushed,
        compositions: [...flushed.compositions, comp],
        activeCompositionId: comp.id,
        settings: comp.settings,
        tracks: comp.tracks,
        clips: comp.clips,
        markers: comp.markers,
        updatedAt: Date.now(),
      },
      playhead: 0,
      isPlaying: false,
      selection: [],
      selectedGap: null,
      inPoint: null,
      outPoint: null,
    });
    return comp.id;
  },

  renameComposition: (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const project = flushActiveComposition(get().project);
    set({
      project: {
        ...project,
        compositions: project.compositions.map((c) =>
          c.id === id ? { ...c, name: trimmed } : c
        ),
        updatedAt: Date.now(),
      },
    });
  },

  duplicateComposition: (id) => {
    const flushed = flushActiveComposition(get().project);
    const src = flushed.compositions.find((c) => c.id === id);
    if (!src) return null;
    const copy = {
      ...structuredClone(src),
      id: crypto.randomUUID(),
      name: `${src.name} Copy`,
      tracks: src.tracks.map((t) => ({ ...t, id: crypto.randomUUID() })),
      clips: [] as typeof src.clips,
      markers: src.markers.map((m) => ({ ...m, id: crypto.randomUUID() })),
    };
    const trackMap = new Map(src.tracks.map((t, i) => [t.id, copy.tracks[i]!.id]));
    const linkMap = new Map<string, string>();
    copy.clips = src.clips.map((c) => {
      let linkGroupId: string | null = null;
      if (c.linkGroupId) {
        if (!linkMap.has(c.linkGroupId)) linkMap.set(c.linkGroupId, crypto.randomUUID());
        linkGroupId = linkMap.get(c.linkGroupId)!;
      }
      return {
        ...structuredClone(c),
        id: crypto.randomUUID(),
        trackId: trackMap.get(c.trackId) ?? copy.tracks[0]!.id,
        linkGroupId,
      };
    });

    set({
      project: {
        ...flushed,
        compositions: [...flushed.compositions, copy],
        activeCompositionId: copy.id,
        settings: copy.settings,
        tracks: copy.tracks,
        clips: copy.clips,
        markers: copy.markers,
        updatedAt: Date.now(),
      },
      playhead: 0,
      isPlaying: false,
      selection: [],
      selectedGap: null,
      inPoint: null,
      outPoint: null,
    });
    return copy.id;
  },

  removeComposition: (id) => {
    const flushed = flushActiveComposition(get().project);
    if (flushed.compositions.length <= 1) return;
    const compositions = flushed.compositions.filter((c) => c.id !== id);
    const next =
      flushed.activeCompositionId === id
        ? compositions[0]!
        : compositions.find((c) => c.id === flushed.activeCompositionId) ?? compositions[0]!;
    set({
      project: {
        ...flushed,
        compositions,
        activeCompositionId: next.id,
        settings: next.settings,
        tracks: next.tracks,
        clips: next.clips,
        markers: next.markers,
        updatedAt: Date.now(),
      },
      playhead: flushed.activeCompositionId === id ? 0 : get().playhead,
      isPlaying: false,
      selection: flushed.activeCompositionId === id ? [] : get().selection,
      selectedGap: null,
      inPoint: flushed.activeCompositionId === id ? null : get().inPoint,
      outPoint: flushed.activeCompositionId === id ? null : get().outPoint,
    });
  },

  renameTrack: (trackId, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const project = get().project;
    set({
      project: {
        ...project,
        tracks: project.tracks.map((t) =>
          t.id === trackId ? { ...t, name: trimmed } : t
        ),
        updatedAt: Date.now(),
      },
    });
  },

  setTrackHeight: (trackId, height) => {
    const h = Math.round(Math.min(240, Math.max(24, height)));
    const project = get().project;
    const track = project.tracks.find((t) => t.id === trackId);
    if (!track || track.height === h) return;
    set({
      project: {
        ...project,
        tracks: project.tracks.map((t) =>
          t.id === trackId ? { ...t, height: h } : t
        ),
        updatedAt: Date.now(),
      },
    });
  },

  addVideoTrack: (name, relativeToTrackId) => {
    const project = get().project;
    const track = createTrack('video', name.trim() || suggestTrackName(project.tracks, 'video'));
    const tracks = [...project.tracks];
    let idx = 0;
    if (relativeToTrackId) {
      const at = tracks.findIndex((t) => t.id === relativeToTrackId);
      if (at >= 0) {
        idx = tracks[at]!.kind === 'video' ? at : tracks.findIndex((t) => t.kind === 'audio');
        if (idx < 0) idx = tracks.length;
      }
    }
    tracks.splice(idx, 0, track);
    set({ project: { ...project, tracks, updatedAt: Date.now() } });
    return track.id;
  },

  addAudioTrack: (name, channels, relativeToTrackId) => {
    const project = get().project;
    const track = createTrack(
      'audio',
      name.trim() || suggestTrackName(project.tracks, 'audio'),
      { channels }
    );
    const tracks = [...project.tracks];
    let idx = tracks.length;
    if (relativeToTrackId) {
      const at = tracks.findIndex((t) => t.id === relativeToTrackId);
      if (at >= 0) {
        idx = tracks[at]!.kind === 'audio' ? at + 1 : tracks.length;
      }
    } else {
      const lastAudio = [...tracks].map((t, i) => ({ t, i })).reverse().find((x) => x.t.kind === 'audio');
      idx = lastAudio ? lastAudio.i + 1 : tracks.length;
    }
    tracks.splice(idx, 0, track);
    set({ project: { ...project, tracks, updatedAt: Date.now() } });
    return track.id;
  },

  setPlayhead: (t) => set({ playhead: Math.max(0, t) }),
  togglePlay: () => set({ isPlaying: !get().isPlaying }),
  setPlaying: (v) => set({ isPlaying: v }),
  stepFrame: (dir) => {
    const { playhead, project, setPlaying, setPlayhead } = get();
    setPlaying(false);
    const frame = 1 / project.settings.frameRate;
    setPlayhead(Math.max(0, playhead + dir * frame));
  },
  goToStart: () => {
    get().setPlaying(false);
    get().setPlayhead(0);
  },
  goToEnd: () => {
    const { project, setPlaying, setPlayhead } = get();
    setPlaying(false);
    setPlayhead(projectDuration(project));
  },
  jumpToNextEdit: (dir) => {
    const { playhead, project, setPlaying, setPlayhead } = get();
    setPlaying(false);
    const edges = new Set<number>([0, projectDuration(project)]);
    for (const c of project.clips) {
      edges.add(c.start);
      edges.add(c.start + c.duration);
    }
    const sorted = [...edges].sort((a, b) => a - b);
    if (dir > 0) {
      const next = sorted.find((t) => t > playhead + 1e-4);
      if (next != null) setPlayhead(next);
    } else {
      const prev = [...sorted].reverse().find((t) => t < playhead - 1e-4);
      if (prev != null) setPlayhead(prev);
    }
  },

  addMarker: (time, label) => {
    const t = time ?? get().playhead;
    const marker = createMarker(t, label);
    const project = get().project;
    set({
      project: {
        ...project,
        markers: [...(project.markers ?? []), marker].sort((a, b) => a.time - b.time),
        updatedAt: Date.now(),
      },
    });
  },
  removeMarker: (id) => {
    const project = get().project;
    set({
      project: {
        ...project,
        markers: (project.markers ?? []).filter((m) => m.id !== id),
        updatedAt: Date.now(),
      },
    });
  },
  jumpToMarker: (dir) => {
    const { playhead, project, setPlaying, setPlayhead } = get();
    setPlaying(false);
    const markers = [...(project.markers ?? [])].sort((a, b) => a.time - b.time);
    if (!markers.length) return;
    if (dir > 0) {
      const next = markers.find((m) => m.time > playhead + 1e-4);
      setPlayhead(next ? next.time : markers[0].time);
    } else {
      const prev = [...markers].reverse().find((m) => m.time < playhead - 1e-4);
      setPlayhead(prev ? prev.time : markers[markers.length - 1].time);
    }
  },

  selectClips: (ids, mode = 'replace') => {
    const project = get().project;
    const expanded = expandWithLinked(project, ids);
    if (mode === 'replace') {
      set({ selection: expanded, selectedGap: null });
      return;
    }
    const cur = new Set(get().selection);
    if (mode === 'add') {
      expanded.forEach((id) => cur.add(id));
    } else {
      const allOn = expanded.every((id) => cur.has(id));
      if (allOn) expanded.forEach((id) => cur.delete(id));
      else expanded.forEach((id) => cur.add(id));
    }
    set({ selection: [...cur], selectedGap: null });
  },
  selectGap: (gap) => set({ selectedGap: gap, selection: [] }),
  deleteSelectedGap: () => {
    const gap = get().selectedGap;
    if (!gap) return;
    set({
      project: closeGap(get().project, gap),
      selectedGap: null,
    });
  },
  setTool: (t) => set({ tool: t }),
  setTimelineZoom: (z) => {
    const min = get().timelineZoomMin;
    set({ timelineZoom: Math.min(800, Math.max(min, z)) });
  },
  setTimelineZoomMin: (z) => {
    const min = Math.max(1, Math.min(800, z));
    const zoom = get().timelineZoom;
    set({
      timelineZoomMin: min,
      timelineZoom: Math.max(min, zoom),
    });
  },
  setTimelineScrollX: (x) => set({ timelineScrollX: Math.max(0, x) }),
  setInPoint: (t) => set({ inPoint: t }),
  setOutPoint: (t) => {
    if (t == null) {
      set({ outPoint: null });
      return;
    }
    // Marking Out with no In → In defaults to start of timeline (0s)
    if (get().inPoint == null) {
      set({ inPoint: 0, outPoint: Math.max(0, t) });
      return;
    }
    set({ outPoint: Math.max(0, t) });
  },

  trimCompToWorkArea: () => {
    const { inPoint, outPoint, project, playhead } = get();
    if (inPoint == null || outPoint == null || outPoint <= inPoint) return;
    const shift = inPoint;
    const newDur = Math.max(1 / Math.max(1, project.settings.frameRate), outPoint - inPoint);

    const clips = project.clips
      .map((c) => ({ ...c, start: c.start - shift }))
      .filter((c) => c.start + c.duration > 1e-4 && c.start < newDur - 1e-4)
      .map((c) => {
        let start = c.start;
        let duration = c.duration;
        let srcIn = c.inPoint;
        if (start < 0) {
          const cut = -start;
          start = 0;
          duration = Math.max(0, duration - cut);
          srcIn += cut;
        }
        if (start + duration > newDur) {
          duration = Math.max(0, newDur - start);
        }
        return {
          ...c,
          start,
          duration,
          inPoint: srcIn,
          outPoint: srcIn + duration,
        };
      })
      .filter((c) => c.duration > 1e-4);

    const markers = (project.markers ?? [])
      .map((m) => ({ ...m, time: m.time - shift }))
      .filter((m) => m.time >= 0 && m.time <= newDur);

    set({
      project: {
        ...project,
        settings: { ...project.settings, duration: newDur },
        clips,
        markers,
        updatedAt: Date.now(),
      },
      playhead: Math.max(0, Math.min(newDur, playhead - shift)),
      isPlaying: false,
      inPoint: null,
      outPoint: null,
      selectedGap: null,
    });
  },

  setTheme: (t) => {
    try {
      localStorage.setItem('cinegrid-theme', t);
    } catch {
      /* ignore */
    }
    document.documentElement.setAttribute('data-theme', t);
    set({ theme: t });
  },
  setInspectorTab: (t) => set({ inspectorTab: t }),
  setSnapEnabled: (v) => set({ snapEnabled: v }),
  setShowWindowManager: (v) => set({ showWindowManager: v }),
  setMediaSelection: (ids) => set({ mediaSelection: ids }),
  setPanelSize: (key, value) => {
    const panelSizes = { ...get().panelSizes, [key]: value };
    persistPanelSizes(panelSizes);
    set({ panelSizes });
  },
  resetPanelSizes: () => {
    const panelSizes = { ...DEFAULT_PANEL_SIZES };
    persistPanelSizes(panelSizes);
    set({ panelSizes });
  },

  addAsset: (asset) =>
    set({
      project: {
        ...get().project,
        assets: [...get().project.assets, asset],
        updatedAt: Date.now(),
      },
    }),
  removeAsset: (id) => {
    get().removeAssets([id]);
  },
  removeAssets: (ids) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    for (const id of ids) {
      forgetAssetUrl(id);
      void deleteMediaBlob(id);
    }
    set({
      project: {
        ...get().project,
        assets: get().project.assets.filter((a) => !idSet.has(a.id)),
        clips: get().project.clips.filter((c) => !c.assetId || !idSet.has(c.assetId)),
        updatedAt: Date.now(),
      },
    });
  },
  interpretAssetFrameRate: (assetIds, fps) => {
    const nextFps = Math.max(1, Math.min(240, fps));
    if (!Number.isFinite(nextFps) || assetIds.length === 0) return;
    const idSet = new Set(assetIds);
    const project = get().project;

    const scaleFor = (asset: MediaAsset) => {
      const old = Math.max(1e-6, asset.fps || asset.nativeFps || 30);
      return old / nextFps;
    };

    const assets = project.assets.map((a) => {
      if (!idSet.has(a.id)) return a;
      if (a.kind !== 'video' && a.kind !== 'image') return a;
      const scale = scaleFor(a);
      return {
        ...a,
        nativeFps: a.nativeFps ?? a.fps,
        fps: nextFps,
        duration: Math.max(1 / nextFps, a.duration * scale),
      };
    });

    const scaleByAssetId = new Map(
      assets
        .filter((a) => idSet.has(a.id))
        .map((a) => {
          const prev = project.assets.find((x) => x.id === a.id)!;
          return [a.id, scaleFor(prev)] as const;
        })
    );

    const mapClip = (c: Clip): Clip => {
      if (!c.assetId || !scaleByAssetId.has(c.assetId)) return c;
      const scale = scaleByAssetId.get(c.assetId)!;
      const inPoint = Math.max(0, c.inPoint * scale);
      const outPoint = Math.max(inPoint + 1 / nextFps, c.outPoint * scale);
      return {
        ...c,
        inPoint,
        outPoint,
        duration: Math.max(1 / nextFps, outPoint - inPoint),
      };
    };

    const compositions = (project.compositions ?? []).map((comp) => ({
      ...comp,
      clips: comp.clips.map(mapClip),
    }));

    set({
      project: {
        ...project,
        assets,
        clips: project.clips.map(mapClip),
        compositions,
        updatedAt: Date.now(),
      },
    });
  },
  createAdjustmentAsset: () => {
    const id = crypto.randomUUID();
    const n =
      get().project.assets.filter((a) => a.kind === 'adjustment').length + 1;
    const asset: MediaAsset = {
      id,
      name: `Adjustment ${n}`,
      kind: 'adjustment',
      url: '',
      mimeType: 'application/x-cinegrid-adjustment',
      duration: 2,
      width: get().project.settings.width,
      height: get().project.settings.height,
      fps: get().project.settings.frameRate,
      nativeFps: get().project.settings.frameRate,
      sizeBytes: 0,
      createdAt: Date.now(),
    };
    get().addAsset(asset);
    return id;
  },
  createSolidAsset: (color = '#808080') => {
    const id = crypto.randomUUID();
    const n = get().project.assets.filter((a) => a.kind === 'solid').length + 1;
    const asset: MediaAsset = {
      id,
      name: `Solid ${n}`,
      kind: 'solid',
      url: '',
      mimeType: 'application/x-cinegrid-solid',
      duration: 2,
      width: get().project.settings.width,
      height: get().project.settings.height,
      fps: get().project.settings.frameRate,
      nativeFps: get().project.settings.frameRate,
      sizeBytes: 0,
      createdAt: Date.now(),
      solidColor: color,
    };
    get().addAsset(asset);
    return id;
  },
  addClipFromAsset: (assetId, trackId, start) => {
    const project = get().project;
    const asset = project.assets.find((a) => a.id === assetId);
    if (!asset) return;

    const track = project.tracks.find((t) => t.id === trackId);
    const duration = asset.duration || 5;
    const newClips: Clip[] = [];
    const selection: string[] = [];

    const preferVideoTrack = () => {
      const videos = project.tracks.filter((t) => t.kind === 'video');
      return (
        videos.find((t) => /^v1$/i.test(t.name.trim())) ||
        [...videos].reverse()[0] ||
        videos[0]
      );
    };
    const preferTopVideoTrack = () => {
      const videos = project.tracks.filter((t) => t.kind === 'video');
      return (
        videos.find((t) => /^v2$/i.test(t.name.trim())) ||
        videos[0] ||
        preferVideoTrack()
      );
    };
    const preferAudioTrack = () => {
      const audios = project.tracks.filter((t) => t.kind === 'audio');
      return audios.find((t) => /^a1$/i.test(t.name.trim())) || audios[0];
    };

    if (asset.kind === 'adjustment') {
      const videoTrack =
        track?.kind === 'video' ? track : preferTopVideoTrack();
      if (!videoTrack) return;
      const clip = createClip({
        trackId: videoTrack.id,
        assetId,
        kind: 'adjustment',
        name: asset.name,
        start,
        duration: asset.duration || 2,
        inPoint: 0,
        outPoint: asset.duration || 2,
      });
      newClips.push(clip);
      selection.push(clip.id);
    } else if (asset.kind === 'solid') {
      const videoTrack =
        track?.kind === 'video' ? track : preferTopVideoTrack();
      if (!videoTrack) return;
      const solidClip = createClip({
        trackId: videoTrack.id,
        assetId,
        kind: 'solid',
        name: asset.name,
        start,
        duration: asset.duration || 2,
        inPoint: 0,
        outPoint: asset.duration || 2,
      });
      solidClip.properties.solidColor = asset.solidColor || '#808080';
      newClips.push(solidClip);
      selection.push(solidClip.id);
    } else if (asset.kind === 'video') {
      const videoTrack =
        track?.kind === 'video' ? track : preferVideoTrack() ?? track;
      const audioTrack = preferAudioTrack();
      if (!videoTrack) return;

      const linkGroupId = crypto.randomUUID();
      const videoClip = createClip({
        trackId: videoTrack.id,
        assetId,
        kind: 'video',
        name: asset.name,
        start,
        duration,
        inPoint: 0,
        outPoint: duration,
        linkGroupId,
      });
      newClips.push(videoClip);
      selection.push(videoClip.id);

      if (audioTrack) {
        const audioClip = createClip({
          trackId: audioTrack.id,
          assetId,
          kind: 'audio',
          name: asset.name,
          start,
          duration,
          inPoint: 0,
          outPoint: duration,
          linkGroupId,
        });
        newClips.push(audioClip);
        selection.push(audioClip.id);
      }
    } else if (asset.kind === 'audio') {
      const audioTrack =
        track?.kind === 'audio' ? track : preferAudioTrack();
      if (!audioTrack) return;
      const clip = createClip({
        trackId: audioTrack.id,
        assetId,
        kind: 'audio',
        name: asset.name,
        start,
        duration,
        inPoint: 0,
        outPoint: duration,
      });
      newClips.push(clip);
      selection.push(clip.id);
    } else {
      // image → video track still
      const videoTrack =
        track?.kind === 'video' ? track : preferVideoTrack() ?? track;
      if (!videoTrack) return;
      const clip = createClip({
        trackId: videoTrack.id,
        assetId,
        kind: 'video',
        name: asset.name,
        start,
        duration,
        inPoint: 0,
        outPoint: duration,
      });
      newClips.push(clip);
      selection.push(clip.id);
    }

    const hasVideoClips = project.clips.some((c) => c.kind === 'video');
    const shouldMatchAspect =
      !hasVideoClips &&
      (asset.kind === 'video' || asset.kind === 'image') &&
      asset.width > 0 &&
      asset.height > 0;

    set({
      project: {
        ...project,
        settings: shouldMatchAspect
          ? { ...project.settings, width: asset.width, height: asset.height }
          : project.settings,
        clips: [...project.clips, ...newClips],
        updatedAt: Date.now(),
      },
      selection,
    });
  },
  addTextClip: (trackId, start) => {
    const clip = createClip({
      trackId,
      kind: 'text',
      name: 'Text',
      start,
      duration: 5,
    });
    set({
      project: {
        ...get().project,
        clips: [...get().project.clips, clip],
        updatedAt: Date.now(),
      },
      selection: [clip.id],
      inspectorTab: 'text',
      tool: 'select',
    });
  },

  cutAtPlayhead: () => {
    const { playhead, project, selection } = get();
    const seed =
      selection.length > 0
        ? selection
        : project.clips
            .filter((c) => playhead > c.start && playhead < c.start + c.duration)
            .map((c) => c.id);
    const targetIds = expandWithLinked(project, seed);

    let clips = [...project.clips];
    const newSelection: string[] = [];

    // Process by link group so left/right halves stay linked
    const seen = new Set<string>();
    for (const id of targetIds) {
      if (seen.has(id)) continue;
      const groupIds = linkedClipIds({ ...project, clips }, id);
      groupIds.forEach((g) => seen.add(g));

      const groupClips = groupIds
        .map((gid) => clips.find((c) => c.id === gid))
        .filter(Boolean) as Clip[];
      if (groupClips.length === 0) continue;

      const leftLink = groupClips[0]?.linkGroupId ? crypto.randomUUID() : null;
      const rightLink = groupClips[0]?.linkGroupId ? crypto.randomUUID() : null;

      for (const clip of groupClips) {
        const parts = splitClip(clip, playhead);
        if (!parts) continue;
        const [left, right] = parts;
        left.linkGroupId = leftLink;
        right.linkGroupId = rightLink;
        clips = clips.filter((c) => c.id !== clip.id).concat([left, right]);
        newSelection.push(left.id, right.id);
      }
    }
    set({
      project: { ...project, clips, updatedAt: Date.now() },
      selection: newSelection,
    });
  },

  rippleDeleteSelected: () => {
    const gap = get().selectedGap;
    if (gap) {
      get().deleteSelectedGap();
      return;
    }
    let project = get().project;
    for (const id of get().selection) {
      project = rippleDelete(project, id);
    }
    set({ project, selection: [] });
  },

  liftDeleteSelected: () => {
    let project = get().project;
    for (const id of get().selection) {
      project = liftDelete(project, id);
    }
    set({ project, selection: [] });
  },

  trimSelected: (edge, time, ripple) => {
    const id = get().selection[0];
    if (!id) return;
    get().trimClipAt(id, edge, time, ripple);
  },
  trimClipAt: (clipId, edge, time, ripple = false) => {
    let project = get().project;
    const ids = linkedClipIds(project, clipId);
    for (const id of ids) {
      const clip = project.clips.find((c) => c.id === id);
      if (!clip) continue;
      const asset = clip.assetId
        ? project.assets.find((a) => a.id === clip.assetId)
        : undefined;
      const frame = 1 / project.settings.frameRate;
      project = trimClip(project, id, edge, time, ripple && id === clipId, {
        minDuration: frame,
        maxSourceOut: asset?.duration,
      });
    }
    set({ project });
  },

  moveSelected: (clipId, start, trackId) => {
    set({ project: moveClip(get().project, clipId, start, trackId) });
  },
  moveClips: (updates) => {
    if (updates.length === 0) return;
    let project = get().project;
    const byId = new Map(updates.map((u) => [u.clipId, u]));
    project = {
      ...project,
      clips: project.clips.map((c) => {
        const u = byId.get(c.id);
        if (!u) return c;
        return {
          ...c,
          start: Math.max(0, u.start),
          trackId: u.trackId ?? c.trackId,
        };
      }),
      updatedAt: Date.now(),
    };
    set({ project });
  },

  unlinkClip: (clipId) => {
    const project = get().project;
    const clip = project.clips.find((c) => c.id === clipId);
    if (!clip?.linkGroupId) return;
    const gid = clip.linkGroupId;
    set({
      project: {
        ...project,
        clips: project.clips.map((c) =>
          c.linkGroupId === gid ? { ...c, linkGroupId: null } : c
        ),
        updatedAt: Date.now(),
      },
    });
  },

  linkSelectedAv: () => {
    const { selection, project } = get();
    const selected = selection
      .map((id) => project.clips.find((c) => c.id === id))
      .filter((c): c is NonNullable<typeof c> => !!c);
    const video = selected.find((c) => c.kind === 'video');
    const audio = selected.find((c) => c.kind === 'audio');
    if (!video || !audio) return;

    const oldGroups = new Set(
      [video.linkGroupId, audio.linkGroupId].filter((g): g is string => !!g)
    );
    const linkGroupId = crypto.randomUUID();

    set({
      project: {
        ...project,
        clips: project.clips.map((c) => {
          if (c.id === video.id || c.id === audio.id) {
            return { ...c, linkGroupId };
          }
          // Detach previous partners so a clip only belongs to one link group
          if (c.linkGroupId && oldGroups.has(c.linkGroupId)) {
            return { ...c, linkGroupId: null };
          }
          return c;
        }),
        updatedAt: Date.now(),
      },
    });
  },

  addCrossDissolveToSelected: () => {
    const id = get().selection[0];
    if (!id) return;
    const project = get().project;
    set({
      project: {
        ...project,
        clips: project.clips.map((c) =>
          c.id === id ? { ...c, transitionOut: createCrossDissolve(0.5) } : c
        ),
        updatedAt: Date.now(),
      },
    });
  },

  updateClipProperties: (clipId, props) => {
    const project = get().project;
    set({
      project: {
        ...project,
        clips: project.clips.map((c) =>
          c.id === clipId
            ? { ...c, properties: { ...c.properties, ...props } }
            : c
        ),
        updatedAt: Date.now(),
      },
    });
  },

  updateClipTransform: (clipId, t) => {
    const project = get().project;
    set({
      project: {
        ...project,
        clips: project.clips.map((c) =>
          c.id === clipId
            ? {
                ...c,
                properties: {
                  ...c.properties,
                  transform: { ...c.properties.transform, ...t },
                },
              }
            : c
        ),
        updatedAt: Date.now(),
      },
    });
  },

  updateClipColor: (clipId, grade) => {
    const project = get().project;
    set({
      project: {
        ...project,
        clips: project.clips.map((c) =>
          c.id === clipId
            ? {
                ...c,
                properties: {
                  ...c.properties,
                  color: {
                    ...c.properties.color,
                    ...grade,
                    lift: { ...c.properties.color.lift, ...(grade.lift ?? {}) },
                    gamma: { ...c.properties.color.gamma, ...(grade.gamma ?? {}) },
                    gain: { ...c.properties.color.gain, ...(grade.gain ?? {}) },
                    qualifier: {
                      ...c.properties.color.qualifier,
                      ...(grade.qualifier ?? {}),
                    },
                  },
                },
              }
            : c
        ),
        updatedAt: Date.now(),
      },
    });
  },

  updateClipAudio: (clipId, audio) => {
    const project = get().project;
    const defaults = defaultAudioSettings();
    set({
      project: {
        ...project,
        clips: project.clips.map((c) => {
          if (c.id !== clipId) return c;
          const cur = c.properties.audio ?? defaults;
          return {
            ...c,
            properties: {
              ...c.properties,
              audio: {
                ...cur,
                ...audio,
                noiseRemoval: { ...cur.noiseRemoval, ...(audio.noiseRemoval ?? {}) },
                reverb: { ...cur.reverb, ...(audio.reverb ?? {}) },
                eq: { ...cur.eq, ...(audio.eq ?? {}) },
                compressor: { ...cur.compressor, ...(audio.compressor ?? {}) },
              },
            },
          };
        }),
        updatedAt: Date.now(),
      },
    });
  },

  updateClip: (clipId, patch) => {
    const project = get().project;
    set({
      project: {
        ...project,
        clips: project.clips.map((c) => (c.id === clipId ? { ...c, ...patch } : c)),
        updatedAt: Date.now(),
      },
    });
  },

  setLayout: (layoutOrId) => {
    const layout =
      typeof layoutOrId === 'string'
        ? LAYOUT_PRESETS.find((l) => l.id === layoutOrId) ?? DEFAULT_PRIMARY_LAYOUT
        : layoutOrId;
    try {
      localStorage.removeItem('cinegrid-layout');
    } catch {
      /* ignore */
    }
    set({ layout });
  },
  setLeader: (v) => set({ isLeader: v }),
  setConnectedTabs: (tabs) => set({ connectedTabs: tabs }),
  hasPanel: (id) => get().layout.panels.includes(id),
  };
});

export function getSelectedClip(): Clip | undefined {
  const { project, selection } = useEditorStore.getState();
  if (!selection[0]) return undefined;
  return project.clips.find((c) => c.id === selection[0]);
}

export { projectDuration };
